
import { useState, useEffect, useContext } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { ReactComponent as Eye } from "bootstrap-icons/icons/eye.svg";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { ReactComponent as PencilSquare } from "bootstrap-icons/icons/pencil-square.svg";
import { ReactComponent as ChevronDown } from "bootstrap-icons/icons/chevron-down.svg";
import { ReactComponent as XCircle } from "bootstrap-icons/icons/x-circle.svg";
import { ReactComponent as Download } from "bootstrap-icons/icons/download.svg";
import { ReactComponent as Search } from "bootstrap-icons/icons/search.svg";
import { ReactComponent as ArrowClockwise } from "bootstrap-icons/icons/arrow-clockwise.svg";
import { ReactComponent as CashStack } from "bootstrap-icons/icons/cash-stack.svg";
import Spinner from "../spinner/Spinner";
import { Link, useNavigate } from "react-router-dom";
import { handleError } from "../../components/utils/AuthenticationCheck";
import { AuthContext } from "../../AuthProvider/AuthProvider";
import axios from "axios";
import ApiServices from "../../api/ApiServices";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import CustomStyles from "../../components/utils/CustomStyles";
import formatDate from "../../components/utils/DateFormatter";
import { downloadCSVFromApi } from "../../components/utils/DownlaodCSVFromApi";

const VendorList = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCSV, setLoadingCSV] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedVendorStatus, setSelectedVendorStatus] = useState("");
  const { permissions } = useContext(AuthContext);

  // Date filter state
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
  });

  const navigate = useNavigate();

  // ==========================- fetch all vendors =================
  useEffect(() => {
    const fetchAllvendorList = async () => {
      setLoading(true);
      try {
        const authData = JSON.parse(localStorage.getItem("auth"));
        const token = authData?.token;
        if (!token) {
          throw new Error("No access token found.");
        }
        const response = await axios.get(ApiServices.getAllVendor, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        });

        const { status, message, data } = response.data;
        if (status === 200) {
          setVendors(data);
          toast.success(message, { toastId: "vendor-fetch-success" });
        } else {
          handleError({ response: { status, data: { message } } }, navigate);
        }
      } catch (error) {
        handleError(error, navigate);
      } finally {
        setLoading(false);
      }
    };

    fetchAllvendorList();
  }, [navigate]);
  // ================================== end of list of vendor ==========================================

  //filter data by name
  const handleFilter = (e) => {
    setFilterText(e.target.value);
    setResetPaginationToggle(!resetPaginationToggle);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Check if both dates are selected
  const areDatesSelected = filters.start_date && filters.end_date;

  // Handle Vendor Status selection
  const handleVendorStatusSelect = (status) => {
    setSelectedVendorStatus(status);
    setIsDropdownOpen(false);
  };

  // Define Expandable Component for Actions
  const ExpandableComponent = ({ data }) => (
    <div className="flex justify-start p-4 relative space-x-4">
      {/* Details Button with Tooltip */}
      {permissions.some(permission => permission.name === 'vendor.view') && (
        <div className="relative group">
          <Link
            to="/admin/vendor/details"
            state={{ vendor_id: data.vendor_id }}
            className="btn btn-info btn-sm"
          >
            <Eye className="w-5 h-5 text-darkGreen3 mx-2" />
          </Link>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:block bg-white  text-darkGreen3 text-xs px-2 py-1 rounded">
            Details
          </div>
        </div>
      )}

      {/* Update Button with Tooltip */}
      {permissions.some(permission => permission.name === 'vendor.edit') && (
        <div className="relative group">
          <Link
            to="/admin/vendor/update"
            state={{ vendorId: data }}
            className="btn btn-info btn-sm"
          >
            <PencilSquare className="w-5 h-5 text-dash4 mr-3" />
          </Link>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:block bg-white  text-dash4 text-xs px-2 py-1 rounded">
            Update
          </div>
        </div>
      )}
    </div>
  );

  const columns = [
    {
      name: "Full Name",
      sortable: true,
      width: "200px",
      cell: (row) => (
        <div>
          <span className="block font-bold">
            {row.firstname && row.lastname
              ? `${row.firstname.toUpperCase()} ${row.lastname.toUpperCase()}`
              : "NOT AVAILABLE"}
          </span>
        </div>
      ),
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
      width: "300px",
    },
    {
      name: "Phone",
      selector: (row) => row.phone_number,
      sortable: true,
      width: "200px",
    },
    {
      name: "Status",
      selector: (row) =>
        row.status ? row.status.toUpperCase() : "NOT AVAILABLE",
      sortable: true,
      cell: (row) => {
        let statusColor = "text-gray-500"; // default for NOT AVAILABLE

        if (row.status) {
          if (row.status.toLowerCase() === "active") {
            statusColor = "text-dash"; //active
          } else if (row.status.toLowerCase() === "blocked") {
            statusColor = "text-dash3"; //blocked
          } else if (row.status.toLowerCase() === "pending") {
            statusColor = "text-dash4"; //pending
          }
        }

        return (
          <span className={statusColor}>
            {row.status ? row.status.toUpperCase() : "NOT AVAILABLE"}
          </span>
        );
      },
      width: "120px",
    },
    {
      name: "Date",
      selector: (row) => row.joined_date ? formatDate(row.joined_date) : "NOT AVAILABLE",
      sortable: true,
      width: "200px",
    },
  ];

  

  const filteredItems = vendors.filter((item) => {
    // Search filter
    const searchableFields = [
      item.firstname || "",
      item.lastname || "",
      item.email || "",
      item.phone_number || "",
      item.status || ""
    ].join(" ").toLowerCase();

    const matchesFilterText = searchableFields.includes(filterText.toLowerCase());

    // Filter data based on selected vendor status
    const matchesVendorStatus =
      !selectedVendorStatus ||
      item.status.toLowerCase() === selectedVendorStatus.toLowerCase();

    // Filter data based on date range
    let matchesDateRange = true;
    if (areDatesSelected) {
      const itemDate = new Date(item.joined_date);
      const startDate = new Date(filters.start_date);
      const endDate = new Date(filters.end_date);
      endDate.setHours(23, 59, 59, 999);
      matchesDateRange = itemDate >= startDate && itemDate <= endDate;
    }

    return matchesFilterText && matchesVendorStatus && matchesDateRange;
  });

  const data = filteredItems.map((vendor) => ({
    joined_date: vendor.joined_date,
    firstname: vendor.firstname
      ? vendor.firstname.toUpperCase()
      : "NOT AVAILABLE",
    lastname: vendor.lastname ? vendor.lastname.toUpperCase() : "NOT AVAILABLE",
    email: vendor.email ? vendor.email.toUpperCase() : "NOT AVAILABLE",
    phone_number: vendor.phone_number ? vendor.phone_number : "NOT AVAILABLE",
    status: vendor.status ? vendor.status.toUpperCase() : "NOT AVAILABLE",
    vendor_id: vendor.vendor_id,
  }));

  // Sort data based on creation date
  const sortedData = [...data].sort((a, b) => {
    const dateA = new Date(a.joined_date);
    const dateB = new Date(b.joined_date);
    return dateB - dateA;
  });

  // Handle CSV Download - Server-side filtered by date
  const handleDownloadCSV = async () => {
    if (!areDatesSelected) {
      toast.error("Please select both start and end dates to download CSV");
      return;
    }

    // Validate date range
    const startDate = new Date(filters.start_date);
    const endDate = new Date(filters.end_date);

    if (startDate > endDate) {
      toast.error("Start date must be before end date");
      return;
    }

    // limit date range to a year
    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    if (daysDiff > 365) {
      toast.error("Date range cannot exceed 1 year");
      return;
    }

    setLoadingCSV(true);

    try {
      const authData = JSON.parse(localStorage.getItem("auth"));
      const token = authData?.token;

      if (!token) {
        toast.error("No access token found");
        return;
      }

      // Prepare download payload
      const downloadPayload = {
        start_date: filters.start_date,
        end_date: filters.end_date,
        download: 1,
      };

      // Create filename with date range
      const startDateFormatted = filters.start_date.replace(/-/g, '_');
      const endDateFormatted = filters.end_date.replace(/-/g, '_');
      const filename = `vendors_${startDateFormatted}_to_${endDateFormatted}.csv`;

      // Call the download function
      await downloadCSVFromApi(
        ApiServices.getAllVendor,
        downloadPayload,
        token,
        {
          filename: filename,
          columnsToRemove: [], // Add sensitive columns to exclude 
          maxRows: 50000
        }
      );

    } catch (error) {
      // console.error("Download error:", error);
      handleError(error, navigate);
      if (error.message && error.message.includes("CSV")) {
        toast.error("Failed to process CSV file");
      } else if (error.message && error.message.includes("network")) {
        toast.error("Network error during download");
      }
    } finally {
      setLoadingCSV(false);
    }
  };

  // Clear date filters
  const handleClearDates = () => {
    setFilters({
      start_date: "",
      end_date: "",
    });
    toast.success("Date filters cleared");
  };

  return (
    <div>
      <MainLayouts>
        <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
          <section
            className="w-full pl-[3%] pr-[3%] pt-3 my-3"
            style={{ backgroundColor: "#F7F7F7" }}
          >
            <nav className="bg-gray-200 py-0 px-0 rounded-lg ">
              <ol className="list-none p-0 inline-flex mb-2">
                <li className="flex items-center">
                  <p className="text-blue-500 hover:text-blue-600 text-darkGreen3">
                    Vendor
                  </p>
                  <ChevronRightIcon />
                </li>
                <li className="flex items-center">
                  <p className="text-orange1">List</p>
                </li>
              </ol>
            </nav>
          </section>

          {/* Filter Controls Section - Responsive */}
          <div className="pb-4 bg-white dark:bg-gray-900">
            {/* DESKTOP FILTERS (md and up) - All in one row */}
            <div className="hidden md:flex flex-wrap items-center gap-3">
              {/* Search Input Field with Icon */}
              <div className="relative flex-grow min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="search"
                    className="block w-full pl-10 pr-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    placeholder="Search ..."
                    value={filterText}
                    onChange={handleFilter}
                  />
                </div>
              </div>

              {/* Vendor Status Dropdown */}
              <div className="relative min-w-[180px]">
                <div>
                  {permissions.some(permission => permission.name === 'vendor.view') && (
                    <button
                      type="button"
                      className="bg-darkGreen3 inline-flex w-full justify-between items-center rounded-md px-3 py-2.5 text-sm font-semibold text-dGrey3"
                      id="menu-button"
                      aria-expanded={isDropdownOpen}
                      aria-haspopup="true"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    >
                      <span className="truncate">
                        {selectedVendorStatus === "active" ? "Active" :
                          selectedVendorStatus === "blocked" ? "Blocked" :
                            selectedVendorStatus === "pending" ? "Pending" :
                              "Vendor Status"}
                      </span>
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                </div>
                {isDropdownOpen && (
                  <div
                    className="absolute right-0 z-10 mt-1 w-full origin-top-right divide-y divide-gray-100 rounded-md bg-lGrey shadow-lg ring-1 ring-black1 ring-opacity-5 focus:outline-none"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="menu-button"
                  >
                    <div
                      className="py-1 rounded-md"
                      role="none"
                      style={{ backgroundColor: "white" }}
                    >
                      <button
                        onClick={() => handleVendorStatusSelect("active")}
                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-darkGreen3 text-left"
                        role="menuitem"
                      >
                        Active
                      </button>
                      <button
                        onClick={() => handleVendorStatusSelect("blocked")}
                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-darkGreen3 text-left"
                        role="menuitem"
                      >
                        Blocked
                      </button>
                      <button
                        onClick={() => handleVendorStatusSelect("pending")}
                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-darkGreen3 text-left"
                        role="menuitem"
                      >
                        Pending
                      </button>
                      <button
                        type="button"
                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-darkGreen3 text-left"
                        onClick={() => handleVendorStatusSelect("")}
                      >
                        All Status
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Start Date Input */}
              <div className="min-w-[150px]">
                <div className="relative">
                  <input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => handleFilterChange("start_date", e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-orange1 focus:border-transparent"
                    max={filters.end_date || undefined}
                    placeholder="Start Date"
                  />
                </div>
              </div>

              {/* End Date Input */}
              <div className="min-w-[150px]">
                <div className="relative">
                  <input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => handleFilterChange("end_date", e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-orange1 focus:border-transparent"
                    min={filters.start_date || undefined}
                    placeholder="End Date"
                  />
                </div>
              </div>

              {/* Clear Dates Button - Only show when dates are selected */}
              {areDatesSelected && (
                <div className="min-w-[120px]">
                  <button
                    onClick={handleClearDates}
                    className="w-full inline-flex justify-center items-center gap-x-2 px-4 py-2.5 text-sm font-medium text-lGrey bg-black1 rounded-lg hover:bg-gray-800 transition-colors duration-200"
                  >
                    <XCircle className="w-4 h-4" />
                    Clear Dates
                  </button>
                </div>
              )}

              {/* Download Button */}
              <div className="min-w-[160px]">
                <button
                  onClick={handleDownloadCSV}
                  className={`w-full inline-flex justify-center items-center gap-x-2 rounded-md px-4 py-2.5 text-sm font-semibold transition-colors duration-200 ${areDatesSelected
                    ? "bg-orange1 text-lGrey3 hover:bg-orange-600 cursor-pointer"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                  disabled={!areDatesSelected || loadingCSV}
                  title={!areDatesSelected ? "Select start and end dates to enable download" : ""}
                >
                  {loadingCSV ? (
                    <>
                      <ArrowClockwise className="w-4 h-4 text-white animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Download CSV
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* MOBILE FILTERS (below md) - Stacked layout */}
            <div className="md:hidden flex flex-col gap-3">
              {/* Search - Full width on mobile */}
              <div className="w-full">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="search"
                    className="block w-full pl-10 pr-3 py-3 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50"
                    placeholder="Search vendors..."
                    value={filterText}
                    onChange={handleFilter}
                  />
                </div>
              </div>

              {/* Filters grid for mobile */}
              <div className="grid grid-cols-2 gap-3">
                {/* Vendor Status Dropdown - Full width on mobile */}
                <div className="col-span-2">
                  <div>
                    {permissions.some(permission => permission.name === 'vendor.view') && (
                      <button
                        type="button"
                        className="bg-darkGreen3 inline-flex w-full justify-between items-center rounded-md px-4 py-3 text-sm font-semibold text-dGrey3"
                        id="mobile-menu-button"
                        aria-expanded={isDropdownOpen}
                        aria-haspopup="true"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      >
                        <span className="truncate">
                          {selectedVendorStatus === "active" ? "Active" :
                            selectedVendorStatus === "blocked" ? "Blocked" :
                              selectedVendorStatus === "pending" ? "Pending" :
                                "Vendor Status"}
                        </span>
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      </button>
                    )}
                  </div>
                  {isDropdownOpen && (
                    <div
                      className="relative z-10 mt-1 w-full divide-y divide-gray-100 rounded-md bg-lGrey shadow-lg ring-1 ring-black1 ring-opacity-5 focus:outline-none"
                      role="menu"
                      aria-orientation="vertical"
                      aria-labelledby="mobile-menu-button"
                    >
                      <div
                        className="py-1 rounded-md"
                        role="none"
                        style={{ backgroundColor: "white" }}
                      >
                        <button
                          onClick={() => handleVendorStatusSelect("active")}
                          className="block w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 hover:text-darkGreen3 text-left"
                          role="menuitem"
                        >
                          Active
                        </button>
                        <button
                          onClick={() => handleVendorStatusSelect("blocked")}
                          className="block w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 hover:text-darkGreen3 text-left"
                          role="menuitem"
                        >
                          Blocked
                        </button>
                        <button
                          onClick={() => handleVendorStatusSelect("pending")}
                          className="block w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 hover:text-darkGreen3 text-left"
                          role="menuitem"
                        >
                          Pending
                        </button>
                        <button
                          type="button"
                          className="block w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 hover:text-darkGreen3 text-left"
                          onClick={() => handleVendorStatusSelect("")}
                        >
                          All Status
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Start Date - Half width on mobile */}
                <div className="col-span-1">
                  <div className="relative">
                    <input
                      type="date"
                      value={filters.start_date}
                      onChange={(e) => handleFilterChange("start_date", e.target.value)}
                      className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-gray-50"
                      max={filters.end_date || undefined}
                      placeholder="Start"
                    />
                  </div>
                </div>

                {/* End Date - Half width on mobile */}
                <div className="col-span-1">
                  <div className="relative">
                    <input
                      type="date"
                      value={filters.end_date}
                      onChange={(e) => handleFilterChange("end_date", e.target.value)}
                      className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-gray-50"
                      min={filters.start_date || undefined}
                      placeholder="End"
                    />
                  </div>
                </div>

                {/* Clear Dates Button - Full width when shown */}
                {areDatesSelected && (
                  <div className="col-span-2">
                    <button
                      onClick={handleClearDates}
                      className="w-full inline-flex justify-center items-center gap-x-2 px-4 py-3 text-sm font-medium text-lGrey bg-black1 rounded-lg hover:bg-gray-800"
                    >
                      <XCircle className="w-4 h-4" />
                      Clear Dates
                    </button>
                  </div>
                )}

                {/* Download Button - Full width on mobile */}
                <div className="col-span-2">
                  <button
                    onClick={handleDownloadCSV}
                    className={`w-full inline-flex justify-center items-center gap-x-2 rounded-md px-4 py-3 text-sm font-semibold ${areDatesSelected
                      ? "bg-orange1 text-lGrey3 hover:bg-orange-600"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                    disabled={!areDatesSelected || loadingCSV}
                  >
                    {loadingCSV ? (
                      <>
                        <ArrowClockwise className="w-4 h-4 text-white animate-spin" />
                        <span className="ml-2">Downloading...</span>
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        <span className="ml-2">Download CSV</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* DESKTOP help text (md and up) */}
            <div className="hidden md:flex items-center justify-between mt-2">
              {!areDatesSelected && (
                <p className="text-xs text-gray-500">
                  Select start and end dates to enable download
                </p>
              )}
              {areDatesSelected && sortedData.length > 0 && (
                <p className="text-sm text-gray-600">
                  Showing {sortedData.length} vendors from {filters.start_date} to {filters.end_date}
                </p>
              )}
            </div>

            {/* MOBILE help text (below md) */}
            <div className="md:hidden mt-3">
              {!areDatesSelected && (
                <p className="text-xs text-gray-500 text-center mb-2">
                  Select start and end dates to enable download
                </p>
              )}
              {areDatesSelected && sortedData.length > 0 && (
                <p className="text-sm text-gray-600 text-center">
                  Showing {sortedData.length} vendors
                </p>
              )}
            </div>
          </div>

          {loading ? (
            <Spinner loading={loading} />
          ) : (
            <DataTable
              columns={columns}
              data={sortedData}
              pagination
              striped
              highlightOnHover
              customStyles={CustomStyles}
              noDataComponent={
                <div className="text-center py-8">
                  {areDatesSelected ?
                    "No vendors found for the selected date range" :
                    "No vendors found"
                  }
                </div>
              }
              expandableRows
              expandableRowsComponent={ExpandableComponent}
            />
          )}
        </div>
      </MainLayouts>
    </div>
  );
};

export default VendorList;
